import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import type { Variant, VariantDeltas } from '@/lib/api-types';
import { useVariants } from '@/lib/queries/variants';
import { GitBranch } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/performance/variants/')({
  component: VariantsListPage,
});

function statusTone(status: Variant['status']): string {
  if (status === 'active') return 'text-[var(--color-success)]';
  if (status === 'archived') return 'text-[var(--color-mute)]';
  return 'text-[var(--color-ink-soft)]';
}

/** One-line human summary of what a variant changes. */
function describeDeltas(deltas: VariantDeltas): string {
  const parts: string[] = [];
  const categories = deltas.blocked_partners?.categories?.length ?? 0;
  const pickedPartners = Object.values(deltas.blocked_partners?.partner_overrides ?? {}).filter(
    (v) => v === 'block',
  ).length;
  const domains = deltas.blocked_partners?.custom_domains?.length ?? 0;
  const blockedBits = [
    categories > 0 ? `${categories} ${categories === 1 ? 'category' : 'categories'}` : null,
    pickedPartners > 0
      ? `${pickedPartners} ${pickedPartners === 1 ? 'third party' : 'third parties'}`
      : null,
    domains > 0 ? `${domains} ${domains === 1 ? 'domain' : 'domains'}` : null,
  ].filter(Boolean);
  if (blockedBits.length > 0) parts.push(blockedBits.join(' + '));
  const headers = Object.keys(deltas.extra_headers ?? {}).length;
  if (headers > 0) parts.push(`${headers} ${headers === 1 ? 'header' : 'headers'}`);
  if (deltas.url_query) parts.push('URL params');
  return parts.length > 0 ? parts.join(' · ') : 'no changes yet';
}

function VariantRow({ variant }: { variant: Variant }): ReactElement {
  return (
    <Link
      to="/performance/variants/$variantUuid"
      params={{ variantUuid: variant.uuid }}
      className="flex items-center justify-between rounded-md px-3 py-2.5 transition-colors hover:bg-[var(--color-paper-alt)]/40"
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[var(--color-ink)]">{variant.name}</span>
          {variant.deltas.blocked_partners && (
            <span className="rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.04em] text-[var(--color-mute)]">
              Without third parties
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--color-mute)]">
          {describeDeltas(variant.deltas)} · {variant.ends_at ? 'fixed window' : 'open-ended'}
        </div>
      </div>
      <span className={`text-xs font-medium capitalize ${statusTone(variant.status)}`}>
        {variant.status}
      </span>
    </Link>
  );
}

function VariantsBody({ siteUuid }: { siteUuid: string }): ReactElement {
  const variants = useVariants(siteUuid);
  const rows = variants.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-base font-semibold text-[var(--color-ink)]">Variants</h1>
          <p className="text-sm text-[var(--color-mute)]">
            Run your audits with modified page conditions — blocked third parties, headers, URL
            params — compared against your baseline.
          </p>
        </div>
        <Link
          to="/performance/variants/new"
          className="rounded-lg border border-transparent bg-[var(--primary)] px-3 py-1.5 text-sm font-medium text-[var(--primary-foreground)]"
        >
          New variant
        </Link>
      </div>

      <div className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
        {variants.isLoading ? (
          <div className="space-y-2" aria-hidden="true">
            <div className="h-14 rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)]/40" />
            <div className="h-14 rounded-md border border-[var(--color-line-soft)] bg-[var(--color-paper-alt)]/40" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-[var(--color-line)] bg-[var(--color-paper-alt)] px-6 py-12 text-center">
            <GitBranch aria-hidden size={28} className="text-[var(--color-mute)]" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-[var(--color-ink)]">No variants yet</p>
              <p className="text-xs text-[var(--color-mute)]">
                A variant re-runs your audits with a set of config changes — block third parties,
                send a feature-flag header, or test URL params — so you can see what they cost.
              </p>
            </div>
            <Link
              to="/performance/variants/new"
              className="rounded-lg border border-[var(--color-line)] px-3 py-1.5 text-sm font-medium text-[var(--color-ink)]"
            >
              Create your first variant
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-[var(--color-line-soft)]/70">
            {rows.map((variant) => (
              <VariantRow key={variant.uuid} variant={variant} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VariantsListPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Variants" parent={{ label: 'Performance', href: '/performance' }}>
      {siteUuid && <VariantsBody siteUuid={siteUuid} />}
    </SiteIsland>
  );
}
